import React from "react";
import styled from "@emotion/styled";
import Ability from "./Ability";
import DataEntry from "./DataEntry";
import mq from "../utils/mediaQueries";
import useWindowSize from "../hooks/useWindowSize";
import { breakpoints } from "../hooks/useBreakpoint";

const Grid = styled.div`
  flex-grow: 1;
  display: grid;
  grid-template-rows: repeat(max-content);
  grid-template-columns: 1fr;
  grid-template-areas:
    "h1"
    "data1"
    "h2"
    "data2"
    "h3"
    "data3"
    "h4";

  ${mq("sm")} {
    grid-template-rows: repeat(max-content);
    grid-template-columns: 1fr max-content 1fr;
    grid-template-areas:
      "h1 h1 h1"
      "data1 v1 data2"
      "h2 h2 h2"
      "data3 data3 data3"
      "h3 h3 h3";
  }

  ${mq("lg")} {
    grid-template-rows: max-content 1fr max-content;
    grid-template-columns: 1fr max-content 1fr max-content 1fr;
    grid-template-areas:
      "h1 h1 h1 h1 h1"
      "data1 v1 data2 v2 data3"
      "h2 h2 h2 h2 h2";
  }
`;

const Horizontal = styled.div`
  grid-area: ${({ id }) => id};

  height: 0.25rem;
  width: 100%;
  background-color: ${({ theme }) => theme.header};
`;

const Vertical = styled.div`
  grid-area: ${({ id }) => id};

  height: 100%;
  width: 0.25rem;
  background-color: ${({ theme }) => theme.header};
`;

const DataColumn = styled.div`
  grid-area: ${({ id }) => id};

  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  max-width: 100%;

  padding: 0.5rem 0.5rem;

  ${mq("sm")} {
    padding: 0.5rem 1rem;
  }

  > div {
    padding: 0.5rem 0;
  }
`;

const DataRow = styled.div`
  grid-area: ${({ id }) => id};

  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  max-width: 100%;
  align-items: flex-start;
  justify-content: flex-start;

  ${mq("sm")} {
    padding: 0.5rem 1rem;
  }

  ${mq("lg")} {
    flex-direction: column;
    flex-wrap: nowrap;
  }

  > div {
    padding: 0.5rem 0;
    flex-basis: 100%;

    ${mq("sm")} {
      flex-basis: 50%;
    }

    ${mq("lg")} {
      flex-basis: 0;

      :last-child {
        flex-basis: 0;
      }
    }
  }
`;

const DataGrid = ({ character }) => {
  const { width } = useWindowSize();

  return (
    <Grid>
      <Horizontal id={"h1"} />
      <DataColumn id={"data1"}>
        <Ability character={character} />
        {character.startingBonus && (
          <DataEntry
            title={"*** ST@RTING BONUS ***"}
            subtitle={`${character.startingBonus.name}`}
            content={character.startingBonus.description}
          />
        )}
      </DataColumn>
      <DataColumn id={"data2"}>
        <DataEntry
          title={"*** ORIGIN ***"}
          subtitle={`${character.origin.name.toUpperCase()}.`}
          content={character.origin.description}
        />
        <DataEntry
          title={"+++ ORIGIN BENEFIT +++"}
          subtitle={`${character.origin.benefit.name.toUpperCase()}.`}
          content={character.origin.benefit.description}
        />
      </DataColumn>
      <DataRow id={"data3"}>
        <DataEntry
          title={"*** BACKGROUND ***"}
          content={`${character.background}.`}
        />
        <DataEntry title={"*** TRAIT ***"} content={`${character.trait}.`} />
        <DataEntry title={"*** DRIVE ***"} content={`${character.drive}.`} />
        <DataEntry title={"*** LOOKS ***"} content={`${character.looks}.`} />
        <DataEntry
          title={"*** PAST ALLEGIANCE ***"}
          content={`${character.pastAllegiance.name.toUpperCase()}. ${
            character.pastAllegiance.description
          }`}
        />
      </DataRow>
      <Horizontal id={"h2"} />
      {width < breakpoints.md && <Horizontal id={"h3"} />}
      {width < breakpoints.md && <Horizontal id={"h4"} />}
      {width >= breakpoints.sm && <Vertical id={"v1"} />}
      {width >= breakpoints.md && <Vertical id={"v2"} />}
    </Grid>
  );
};

export default DataGrid;
